import type { Socket } from 'socket.io-client'

// Shopper side of the storefront chat contract (docs/engineering/storefront-chat.md), mirroring
// the Shopify theme's frontend/lib/storefront-chat.ts. Public endpoints use plain fetch.

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:80'
export const STORE_URL = 'https://shopusars.com'

export type ChatPage = {
  url: string
  path: string
  page_type: 'search'
  title: string
  search_query: string
}

export type ChatProductCard = {
  handle: string
  title: string
  url: string
  image_url?: string | null
  price?: string | null
  variant_id?: number | null
  available?: boolean | null
}

export type ChatMessage = {
  id: string
  author: 'shopper' | 'team'
  body: string
  product_cards?: ChatProductCard[] | null
  created_at: string
}

export type ChatSession = { token: string; conversation_id: string; messages: ChatMessage[] }

/** The overview's signed opening for a chat (USA-1257): its search, answer and named products. */
export type SearchOverviewChat = {
  query: string
  answer: string
  product_handles: string[]
  signature: string
}

export class ChatHttpError extends Error {
  readonly status: number
  constructor(status: number) {
    super(`Chat request failed with HTTP ${status}.`)
    this.name = 'ChatHttpError'
    this.status = status
  }
}

async function chatRequest<T>(
  path: string,
  { method = 'POST', body, token }: { method?: 'GET' | 'POST'; body?: unknown; token?: string },
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (token) headers.Authorization = `Bearer ${token}`
  const response = await fetch(`${API_BASE_URL}/storefront-chat/${path}`, {
    method,
    mode: 'cors',
    credentials: 'omit',
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!response.ok) throw new ChatHttpError(response.status)
  return (await response.json()) as T
}

/** The store's search page for these words, as the widget reports it. */
export function searchChatPage(query: string): ChatPage {
  const params = new URLSearchParams({ q: query })
  return {
    url: `${STORE_URL}/search?${params.toString()}`,
    path: '/search',
    page_type: 'search',
    title: `Search: ${query}`,
    search_query: query,
  }
}

export function startOverviewChat(overview: SearchOverviewChat) {
  return chatRequest<ChatSession>('sessions', {
    body: { page: searchChatPage(overview.query), search_overview: overview },
  })
}

export function sendChatMessage(token: string, body: string, page: ChatPage) {
  return chatRequest<ChatMessage>('messages', { token, body: { body, page } })
}

export function fetchChatMessagesAfter(token: string, afterId: string) {
  return chatRequest<ChatMessage[]>(`messages?${new URLSearchParams({ after: afterId }).toString()}`, {
    method: 'GET',
    token,
  })
}

/** Updates keep their place; new messages arrive in server order and are appended. */
export function mergeMessages(current: ChatMessage[], incoming: ChatMessage[]) {
  const byId = new Map(current.map((message) => [message.id, message]))
  incoming.forEach((message) => byId.set(message.id, message))
  return [...byId.values()]
}

export function chatPriceLabel(price: string | null | undefined) {
  const amount = Number.parseFloat(price ?? '')
  if (!Number.isFinite(amount)) return null
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

/** A store link in a reply opens the live store page; this page is not the store. */
export function storeHref(raw: string) {
  const value = raw.trim()
  if (value.startsWith('/') && !value.startsWith('//')) return `${STORE_URL}${value}`
  try {
    const url = new URL(value)
    return ['https:', 'http:', 'mailto:', 'tel:'].includes(url.protocol) ? url.href : null
  } catch {
    return null
  }
}

export type ChatSocketHandlers = {
  message: (message: ChatMessage) => void
  typing: (active: boolean) => void
  reconnected: () => void
}

export function connectChatSocket(token: string, handlers: ChatSocketHandlers) {
  let socket: Socket | null = null
  let closed = false
  let connectedOnce = false
  void import('socket.io-client')
    .then(({ io }) => {
      if (closed) return
      socket = io(`${API_BASE_URL}/storefront-chat`, {
        path: '/socket.io/',
        auth: { token },
        withCredentials: false,
      })
      socket.on('connect', () => {
        if (connectedOnce) handlers.reconnected()
        connectedOnce = true
      })
      socket.on('storefront_chat.message', (payload: { message?: ChatMessage }) => {
        if (payload?.message?.id) handlers.message(payload.message)
      })
      socket.on('storefront_chat.typing', (payload: { active?: boolean }) => {
        handlers.typing(payload?.active === true)
      })
    })
    .catch(() => undefined)
  return {
    close() {
      closed = true
      socket?.disconnect()
      socket = null
    },
  }
}
